/**
 * BarcodeScanner — full-screen camera modal that reads Code 128, Code 39,
 * QR, and EAN barcodes via @zxing/browser. Calls `onScan(code)` exactly
 * once per successful read, then unmounts.
 *
 * Used by DailyPasscodeCard to scan the printed register passcode.
 * iOS PWA: needs HTTPS (✓ on Emergent) + camera permission (browser prompt).
 *
 * Falls back to manual entry if camera permission is denied or no cameras
 * are available — typing the printed code from the receipt is always an
 * acceptable backup per user spec "1b, but I like 2c just in case".
 */
import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { X, Camera, Keyboard } from "@phosphor-icons/react";

export default function BarcodeScanner({ onScan, onClose, expectedFormat = "" }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  // Latest-onScan ref. Without this, the camera-init useEffect would
  // re-run on every parent re-render (because `onScan` is recreated
  // each time the parent renders inline), tearing the camera down and
  // restarting it. That manifests as "scanner flashes then returns to
  // camera view" — user report 2026-06-15. Stashing in a ref lets the
  // scan callback always reach the freshest handler without depending
  // on its identity.
  const onScanRef = useRef(onScan);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("camera"); // "camera" | "manual"
  const [manualValue, setManualValue] = useState("");
  // Brief green pulse over the aiming frame when a scan lands — visual
  // analog to the audible beep so crew get confirmation before they
  // even look at the result. (User ask 2026-06-15 "add the barcode
  // scanner sound and confirmation".)
  const [flash, setFlash] = useState(false);

  // Plays the classic "barcode read" beep using the Web Audio API. Two
  // short tones (1100Hz → 1500Hz) that together sound like every Symbol/
  // Zebra register scanner in retail. Zero asset bytes — generated in
  // real time, works offline. Guarded behind a try/catch because iOS
  // Safari throws AbortError if audio is suspended (Low Power Mode etc).
  const playScanBeep = () => {
    try {
      // eslint-disable-next-line no-undef
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const playTone = (freq, startOffset, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "square";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + startOffset);
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + startOffset);
        gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + startOffset + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startOffset + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + startOffset);
        osc.stop(ctx.currentTime + startOffset + duration + 0.02);
      };
      playTone(1100, 0, 0.06);
      playTone(1500, 0.07, 0.08);
      // Close the context after the tones finish so we don't leak audio
      // contexts (iOS caps you at ~6).
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 300);
    } catch { /* swallow — sound is a nice-to-have, never blocks the scan */ }
  };

  // Triggers all three confirmation channels: beep, haptic, visual flash.
  // Called the instant a barcode successfully decodes, before the parent
  // unmounts the modal.
  const fireScanConfirmation = () => {
    playScanBeep();
    try { navigator.vibrate?.(50); } catch { /* ignore */ }
    setFlash(true);
    setTimeout(() => setFlash(false), 280);
  };

  useEffect(() => {
    if (mode !== "camera") return undefined;
    let cancelled = false;
    let activeControls = null;
    let activeStream = null;
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    (async () => {
      try {
        // Get a stream with autofocus + rear-camera preference BEFORE
        // handing the device off to ZXing. iOS Safari defaults to
        // fixed-focus on getUserMedia, which makes close-up paper
        // barcodes blur to mush. Requesting `focusMode: "continuous"`
        // (both as a top-level + advanced constraint for browser
        // coverage) tells iOS to keep refocusing as the user moves
        // the phone — this is the key fix for the "won't focus on
        // the scan" report.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            focusMode: "continuous",
            // eslint-disable-next-line no-undef
            advanced: [{ focusMode: "continuous" }],
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        activeStream = stream;
        // Some Android Chromes need the constraint re-applied via
        // `applyConstraints` after the track is live — silent no-op
        // on iOS where the original constraint already took.
        try {
          const track = stream.getVideoTracks()[0];
          await track?.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
        } catch { /* not supported, fine */ }

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute("playsinline", "true");
          await videoRef.current.play().catch(() => {});
        }

        activeControls = await reader.decodeFromStream(
          stream,
          videoRef.current,
          (result, _err) => {
            if (cancelled) return;
            if (result) {
              const text = result.getText();
              if (text) {
                cancelled = true;
                // Capture up to 3 still frames around the moment of
                // barcode lock so OCR has multiple chances at reading
                // the printed date. Camera autofocus often shifts the
                // microsecond after the barcode pins, so a single
                // frame can be slightly blurred — three frames at 0/
                // 70/140ms recovers most cases. (User report 2026-02-14
                // "OCR was supposed to be primary not fallback".)
                const v = videoRef.current;
                const grab = () => {
                  try {
                    if (!v || !v.videoWidth || !v.videoHeight) return null;
                    const canvas = document.createElement("canvas");
                    canvas.width = v.videoWidth;
                    canvas.height = v.videoHeight;
                    canvas.getContext("2d").drawImage(v, 0, 0);
                    return canvas.toDataURL("image/jpeg", 0.92);
                  } catch { return null; }
                };
                const frames = [];
                const f1 = grab();
                if (f1) frames.push(f1);
                // Beep + buzz + green flash — all three fire in parallel
                // so the crew know the scan landed even if they're not
                // looking at the screen.
                fireScanConfirmation();
                Promise.resolve()
                  .then(() => new Promise((r) => setTimeout(r, 70)))
                  .then(() => { const f = grab(); if (f) frames.push(f); })
                  .then(() => new Promise((r) => setTimeout(r, 70)))
                  .then(() => { const f = grab(); if (f) frames.push(f); })
                  .then(() => {
                    try { activeControls?.stop(); } catch { /* ignore */ }
                    try { activeStream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
                    // Read the latest handler via ref so we don't capture
                    // a stale closure if the parent has re-rendered.
                    onScanRef.current?.(text, frames);
                  });
              }
            }
          }
        );
      } catch (e) {
        if (cancelled) return;
        const msg = e?.name === "NotAllowedError"
          ? "Camera access denied. You can type the code from your receipt instead."
          : e?.name === "NotFoundError"
            ? "No camera detected. Type the code from your receipt instead."
            : "Couldn't start the camera. Type the code instead.";
        setError(msg);
        setMode("manual");
      }
    })();

    return () => {
      cancelled = true;
      try { activeControls?.stop(); } catch { /* ignore */ }
      try { activeStream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    };
    // `onScan` is intentionally NOT in the deps — it's a callback whose
    // identity changes on every parent render but whose behavior we want
    // to read fresh via `onScanRef`. Including it would cause the camera
    // to restart on every parent re-render (see the ref comment above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return (
    <div className="fixed inset-0 z-[200] bg-black flex flex-col" data-testid="barcode-scanner">
      <div className="flex items-center justify-between px-4 py-3 text-white bg-black/80">
        <div>
          <p className="text-sm font-black uppercase tracking-wider">Scan Daily Passcode</p>
          <p className="text-[11px] text-white/60">Keep the barcode <span className="font-bold text-emerald-300">AND</span> the printed date in view</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          data-testid="scanner-close"
        >
          <X weight="bold" className="w-5 h-5" />
        </button>
      </div>

      {mode === "camera" ? (
        <div className="flex-1 relative bg-black">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
            onClick={async () => {
              // Tap-to-refocus — iOS Safari's getUserMedia sometimes
              // settles to fixed focus despite the continuous hint.
              // A re-applyConstraints on user tap forces the camera
              // to reacquire focus on whatever's centered.
              try {
                const stream = videoRef.current?.srcObject;
                const track = stream?.getVideoTracks?.()[0];
                if (track?.applyConstraints) {
                  await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
                }
              } catch { /* not supported, no-op */ }
            }}
            data-testid="scanner-video"
          />
          {/* Aiming frame — tightened from the previous huge frame so
              the user naturally points closer to the barcode (closer
              subject = better autofocus on iOS). The frame flashes
              green when a scan lands as visual confirmation of the
              audible beep + haptic buzz. */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              className={`w-72 max-w-[80%] h-24 border-4 rounded-2xl shadow-2xl transition-colors duration-150 ${
                flash ? "border-emerald-400 bg-emerald-400/20" : "border-white/90"
              }`}
              data-testid="scanner-aiming-frame"
            />
          </div>
          <p className="absolute top-20 left-1/2 -translate-x-1/2 text-white text-[11px] font-bold uppercase tracking-wider bg-black/60 px-3 py-1 rounded-full">
            Tap screen to refocus
          </p>
          <button
            type="button"
            onClick={() => setMode("manual")}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white text-slate-900 px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 shadow-2xl"
            data-testid="scanner-manual-fallback"
          >
            <Keyboard weight="bold" className="w-4 h-4" /> Type it instead
          </button>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center p-6 bg-slate-900">
          <div className="w-full max-w-sm bg-white rounded-2xl p-5 space-y-3">
            {error && (
              <p className="text-xs text-rose-700 bg-rose-50 px-3 py-2 rounded-lg" data-testid="scanner-error">
                {error}
              </p>
            )}
            <label className="block text-sm font-bold text-slate-900">
              Enter the passcode printed on your receipt
            </label>
            <input
              autoFocus
              type="text"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value.toUpperCase())}
              placeholder={expectedFormat || "EX: 123456ABC0/XYZ9DEF1GH"}
              className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg font-mono text-sm focus:outline-none focus:border-blue-500"
              data-testid="scanner-manual-input"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode("camera")}
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm font-bold text-slate-700 flex items-center justify-center gap-1"
                data-testid="scanner-back-to-camera"
              >
                <Camera weight="bold" className="w-4 h-4" /> Camera
              </button>
              <button
                type="button"
                onClick={() => {
                  if (manualValue.trim()) {
                    fireScanConfirmation();
                    onScanRef.current?.(manualValue.trim());
                  }
                }}
                disabled={!manualValue.trim()}
                className="flex-1 fb-btn-primary disabled:opacity-50"
                data-testid="scanner-manual-submit"
              >
                Claim
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
